import {
  DialogModal,
  Divider,
  Drawer,
  Grid,
  Loader,
  Stepper,
} from "@pushpress/shared-components";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { PlanType } from "../../../apis/subscriptionApi";
import { DrawerPanel } from "../../../components/common/DrawerPanel";
import {
  convertToCreatePlanBody,
  convertToPlanForm,
  convertToUpdatePlanBody,
  createPlanFormDefaultValues,
  getPlanDrawerTitle,
} from "./PlanDrawer.utils";
import BasicSection from "./sections/BasicSection";
import BillingSection from "./sections/BillingSection";
import SalesSection from "./sections/SalesSection";
import ClassesSection from "./sections/ClassesSection";
import OtherSection from "./sections/OtherSection";
import AppointmentsSection from "./sections/AppointmentsSection";
import {
  usePlanQuery,
  usePostPlanMutation,
  usePutPlanMutation,
} from "../../../apis/subscriptionApi.hooks";
import { toast } from "react-toastify";
import { defaultToastOptions, extractError } from "../../../utils/misc";
import { PlanForm } from "./PlanDrawer.types";
import UpdateSubscriptionsModal from "./UpdateSubscriptionsModal/UpdateSubscriptionsModal";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider";
import { DrawerPanelRef } from "../../../components/common/DrawerPanel/DrawerPanel";
import { PlanTemplate } from "../PlanTemplates/plan-templates";

export interface PlanDrawerProps {
  visible: boolean;
  duplicate?: boolean;
  planUuid?: string;
  planType?: PlanType;
  planTemplate?: PlanTemplate;
  onClose: VoidFunction;
  onSuccess?: VoidFunction;
}

function PlanDrawer(props: PlanDrawerProps) {
  const { visible } = props;
  return (
    <Drawer
      visible={visible}
      anchor="right"
      width="400px"
      overflow="visible"
      disableClickAway
      disableEscapeKeyDown
    >
      <PlanDrawerContent {...props} />
    </Drawer>
  );
}

function PlanDrawerContent(props: PlanDrawerProps) {
  const {
    planUuid,
    planType: planTypeProp,
    planTemplate,
    duplicate,
    onClose,
    onSuccess,
  } = props;

  const { trackEvent } = useAnalytics();

  const drawerPanelRef = useRef<DrawerPanelRef>(null);

  const [confirmCloseVisible, setConfirmCloseVisible] = useState(false);

  const [updateSubscriptionsVisible, setUpdateSubscriptionsVisible] =
    useState(false);

  const [planType, setPlanType] = useState<PlanType>(
    planTypeProp ?? "recurring",
  );

  const [step, setStep] = useState(0);

  const { data: plan } = usePlanQuery(
    planUuid!,
    { include: ["activeSubscriptionsCount"] },
    {
      enabled: !!planUuid,
    },
  );

  const createPlanMutation = usePostPlanMutation();

  const updatePlanMutation = usePutPlanMutation();

  const methods = useForm<PlanForm>({
    mode: "onTouched",
    defaultValues: createPlanFormDefaultValues(planType, planTemplate),
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
    getValues,
  } = methods;

  const [
    price,
    sessionsCount,
    sessionsPeriod,
    attendanceLimit,
    priorityHoursInAdvance,
    perPersonLimitNum,
    hasFixedBillingDayOfMonth,
    fixedBillingDayOfMonth,
    forcePurchaseStartDate,
  ] = useWatch({
    control,
    name: [
      "price",
      "sessionsCount",
      "sessionsPeriod",
      "attendanceLimit",
      "priorityHoursInAdvance",
      "perPersonLimitNum",
      "hasFixedBillingDayOfMonth",
      "fixedBillingDayOfMonth",
      "forcePurchaseStartDate",
    ],
  });

  const handleClose = () => {
    if (isDirty) {
      setConfirmCloseVisible(true);
    } else {
      trackEvent("Plan Drawer Closed", {
        plan_uuid: planUuid,
        plan_type: planType,
        is_duplicate: !!duplicate,
        from_template: !!planTemplate,
        template_id: planTemplate?.id,
        with_changes: false,
      });
      onClose();
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleNext = () => {
    const next = () => {
      setStep(step + 1);
      drawerPanelRef.current?.scrollToTop();
    };
    handleSubmit(next)();
  };

  const savePlan = async (values: PlanForm) => {
    if (!planType) {
      throw new Error("Plan type is required");
    }
    try {
      const body = convertToCreatePlanBody(planType, values);
      const response = await createPlanMutation.mutateAsync({
        body,
      });

      // Determine the original price and whether it was modified based on the source
      let originalPrice = null;
      let priceModified = false;

      if (duplicate) {
        originalPrice = originalPlanPrice;
        priceModified = price !== originalPlanPrice;
      } else if (planTemplate) {
        originalPrice = planTemplate.recommendedPrice;
        priceModified = price !== planTemplate.recommendedPrice;
      }

      trackEvent("Plan Create Success", {
        plan_uuid: response?.data?.planUuid,
        plan_type: planType,
        saved_price: price,
        original_price: originalPrice,
        price_modified: priceModified,
        is_duplicate: !!duplicate,
        from_template: !!planTemplate,
        template_id: planTemplate?.id,
        source: duplicate ? "duplicate" : planTemplate ? "template" : "scratch",
      });

      onSuccess?.();
      onClose?.();
      toast.success("Plan created successfully", defaultToastOptions);
    } catch (error) {
      trackEvent("Plan Create Failure", {
        plan_type: planType,
        is_duplicate: !!duplicate,
        from_template: !!planTemplate,
        template_id: planTemplate?.id,
      });
      toast.error(extractError(error), defaultToastOptions);
    }
  };

  const updatePlan = async (
    values: PlanForm,
    update?: {
      price: boolean;
      sessions: boolean;
    },
  ) => {
    if (!planUuid) {
      throw new Error("Plan uuid is required");
    }
    if (!planType) {
      throw new Error("Plan type is required");
    }
    try {
      const body = convertToUpdatePlanBody(planUuid, planType, values, update);
      await updatePlanMutation.mutateAsync({
        body,
      });
      trackEvent("Plan Edit Success", {
        plan_uuid: planUuid,
        plan_type: planType,
        price, // New price
        original_price: plan?.amount, // Original price before edit
        price_modified: price !== plan?.amount, // Whether price was changed
        update_price: update?.price,
        update_sessions: update?.sessions,
        with_changes: isDirty,
      });
      onSuccess?.();
      onClose?.();
      toast.success("Plan updated successfully", defaultToastOptions);
    } catch (error) {
      trackEvent("Plan Edit Failure", {
        plan_uuid: planUuid,
        plan_type: planType,
      });
      toast.error(extractError(error), defaultToastOptions);
    }
  };

  const handleSave = () => {
    const save = async (values: PlanForm) => {
      // if planUuid is present, it means we are editing an existing plan
      // but when duplicate is true, we are creating a new plan based on an existing one
      if (planUuid && !duplicate) {
        await updatePlan(values);
      } else {
        await savePlan(values);
      }
    };
    handleSubmit(save)();
  };

  const handleOpenUpdateSubscriptionsModal = () => {
    setUpdateSubscriptionsVisible(true);
  };

  const handleConfirmUpdateSubscriptions = (update: {
    price: boolean;
    sessions: boolean;
  }) => {
    const values = getValues();
    updatePlan(values, update);
  };

  // Store the original plan price for tracking purposes
  const [originalPlanPrice, setOriginalPlanPrice] = useState<number | null>(
    null,
  );

  // set the plan type and populate the form when editing
  useEffect(() => {
    if (planUuid && plan) {
      setPlanType(plan.type);
      if (duplicate) {
        setOriginalPlanPrice(plan.amount);
      }
      reset(convertToPlanForm(plan, duplicate));
    }
  }, [reset, planUuid, plan, duplicate]);

  if (!planType) {
    return (
      <Grid container justifyContent="center" className="p-6">
        <Loader />
      </Grid>
    );
  }

  const title = getPlanDrawerTitle(planType);

  const saveLoading =
    createPlanMutation.isLoading || updatePlanMutation.isLoading;

  const hasPriceChanged = plan?.amount !== price;

  const hasSessionsCountChanged =
    plan?.limitCheckinsQuantity !== sessionsCount ||
    plan?.limitCheckinsType !== sessionsPeriod?.value;

  const hasLimitCheckinsChanged =
    plan?.limitCheckins !== (attendanceLimit?.value === "limited");

  const editing = !!planUuid && !duplicate;

  const shouldConfirmUpdate =
    editing &&
    plan?.activeSubscriptionsCount > 0 &&
    (hasLimitCheckinsChanged || hasPriceChanged || hasSessionsCountChanged);

  return (
    <>
      <DrawerPanel
        ref={drawerPanelRef}
        title={title}
        saving={false}
        noBodyPadding
        onClose={handleClose}
      >
        <FormProvider {...methods}>
          <Stepper
            currentStep={step}
            stickyFooter={editing}
            saveLoading={saveLoading}
            onBack={handleBack}
            onNext={handleNext}
            onSave={
              shouldConfirmUpdate
                ? handleOpenUpdateSubscriptionsModal
                : handleSave
            }
          >
            <Stepper.Step>
              <BasicSection control={control} />
              <Divider marginRight={-24} />
              <BillingSection control={control} planType={planType} />
              <Divider marginRight={-24} />
              <ClassesSection control={control} planType={planType} />
              {planType !== "session-pack" && (
                <>
                  <Divider marginRight={-24} />
                  <AppointmentsSection control={control} />
                </>
              )}
              <Divider marginRight={-24} />
              <OtherSection control={control} planType={planType}/>
            </Stepper.Step>
            <Stepper.Step>
              <SalesSection control={control} />
            </Stepper.Step>
          </Stepper>
        </FormProvider>
      </DrawerPanel>
      <DialogModal
        title="Discard unsaved changes?"
        visible={confirmCloseVisible}
        primaryBtnTitle="Discard"
        secondaryBtnTitle="Go back"
        primaryHandler={() => {
          trackEvent("Plan Drawer Closed", {
            plan_uuid: planUuid,
            plan_type: planType,
            is_duplicate: !!duplicate,
            from_template: !!planTemplate,
            template_id: planTemplate?.id,
            with_changes: true,
            changes_discarded: true,
          });
          onClose();
        }}
        secondaryHandler={() => setConfirmCloseVisible(false)}
        closeModal={() => setConfirmCloseVisible(false)}
      />
      {planUuid && (
        <UpdateSubscriptionsModal
          hasLimitCheckinsChanged={hasLimitCheckinsChanged}
          hasPriceChanged={hasPriceChanged}
          hasSessionsCountChanged={hasSessionsCountChanged}
          newLimitCheckins={attendanceLimit?.value === "limited"}
          planUuid={planUuid}
          newPrice={price}
          newSessionsCount={sessionsCount}
          newSessionsPeriod={sessionsPeriod?.value}
          newPriorityBookingHours={priorityHoursInAdvance}
          newPerPersonLimitNum={perPersonLimitNum}
          visible={updateSubscriptionsVisible}
          activeSubscriptionsCount={plan?.activeSubscriptionsCount ?? 0}
          onClose={() => setUpdateSubscriptionsVisible(false)}
          onConfirm={handleConfirmUpdateSubscriptions}
        />
      )}
    </>
  );
}

export default PlanDrawer;
